// events.js – React Query Hooks für Events-Feature
import api from '../config/api';
import { useQuery, useMutation, useQueryClient } from 'react-query';

// Query Keys
export const eventsKeys = {
    list: (params) => ['events', 'list', params || {}],
    detail: (id) => ['events', 'detail', String(id || '')],
    mine: () => ['events', 'mine'],
    adminList: (params) => ['events', 'admin', params || {}]
};

// Öffentliche Event-Liste
export function useEventsList(params) {
    return useQuery(
        eventsKeys.list(params),
        async () => {
            const res = await api.get('/api/events', { params });
            return res.data;
        },
        // When params are not provided, do not fetch.
        // This allows consumers (e.g. Profile page) to disable the query entirely.
        { keepPreviousData: true, enabled: !!params }
    );
}

// Einzelnes Event (öffentlich)
export function useEvent(id) {
    return useQuery(
        eventsKeys.detail(id),
        async () => {
            const res = await api.get(`/api/events/${encodeURIComponent(id)}`);
            return res.data?.event;
        },
        { enabled: !!id }
    );
}

// Eigene Events (auth)
export function useMyEvents() {
    return useQuery(
        eventsKeys.mine(),
        async () => {
            const res = await api.get('/api/events/_mine/list');
            return res.data?.events || [];
        }
    );
}

// Event anlegen (auth, full/admin)
export function useCreateEvent() {
    const qc = useQueryClient();
    return useMutation(
        async (payload) => {
            const res = await api.post('/api/events', payload);
            return res.data?.event;
        },
        {
            onSuccess: () => {
                qc.invalidateQueries(eventsKeys.list({}));
                qc.invalidateQueries(eventsKeys.mine());
            }
        }
    );
}

// Event bearbeiten (auth, full/admin)
export function useUpdateEvent() {
    const qc = useQueryClient();
    return useMutation(
        async ({ id, ...payload }) => {
            const res = await api.put(`/api/events/${encodeURIComponent(id)}`, payload);
            return res.data?.event;
        },
        {
            onSuccess: (_data, vars) => {
                qc.invalidateQueries(eventsKeys.detail(vars?.id));
                qc.invalidateQueries(eventsKeys.list({}));
                qc.invalidateQueries(eventsKeys.mine());
            }
        }
    );
}

// Event löschen (auth, full/admin)
export function useDeleteEvent() {
    const qc = useQueryClient();
    return useMutation(
        async (id) => {
            await api.delete(`/api/events/${encodeURIComponent(id)}`);
            return id;
        },
        {
            onSuccess: (id) => {
                qc.invalidateQueries(eventsKeys.list({}));
                qc.invalidateQueries(eventsKeys.mine());
                qc.invalidateQueries(eventsKeys.detail(id));
            }
        }
    );
}

// Admin: Events-Liste (Moderation)
export function useAdminEvents(params) {
    return useQuery(
        eventsKeys.adminList(params),
        async () => {
            const res = await api.get('/api/events/_admin/list', { params });
            return res.data?.events || [];
        },
        { enabled: !!params }
    );
}

// Admin: Status-Update
export function useAdminUpdateEventStatus() {
    const qc = useQueryClient();
    return useMutation(
        async ({ id, status }) => {
            const res = await api.put(`/api/events/_admin/${encodeURIComponent(id)}/status`, { status });
            return res.data?.event;
        },
        {
            onSuccess: (_data, vars) => {
                qc.invalidateQueries(eventsKeys.adminList({}));
                qc.invalidateQueries(eventsKeys.detail(vars?.id));
                qc.invalidateQueries(eventsKeys.list({}));
            }
        }
    );
}
